import React, { useState } from "react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { ref, update } from "firebase/database";

export default function UserAuth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // --- Sync User Info to Realtime Database ---
  const syncUserToDb = async (user) => {
    try {
      const userRef = ref(db, `users/${user.uid}/profile`);

      // Admin will see this name instead of "Anonymous"
      const nameFromEmail = user.email ? user.email.split("@")[0] : "User";
      const finalName = user.displayName || nameFromEmail;

      await update(userRef, {
        displayName: finalName,
        email: user.email,
        photo: user.photoURL || null,
        uid: user.uid,
        provider: user.providerData[0]?.providerId || "password",
        lastLogin: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Database Sync Error:", err);
    }
  };

  const handleUserAuth = async (e) => {
    e.preventDefault();
    setError("");

    try {
      let result;
      if (isLogin) {
        // Log In existing user
        result = await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Register new user
        result = await createUserWithEmailAndPassword(auth, email, password);
      }

      // Success! Now sync identity to DB for Admin viewing
      await syncUserToDb(result.user);
    } catch (err) {
      // Precise Error Mapping to fix "Invalid" issues
      console.error("Auth Error Code:", err.code);

      switch (err.code) {
        case "auth/invalid-credential":
          setError("Invalid email or password. Please check and try again.");
          break;
        case "auth/user-not-found":
          setError("No account found with this email. Please Sign Up.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password.");
          break;
        case "auth/email-already-in-use":
          setError("Email already registered. Try logging in.");
          break;
        case "auth/weak-password":
          setError("Password should be at least 6 characters.");
          break;
        default:
          setError("Authentication failed. Please try again.");
      }
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      await syncUserToDb(result.user);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="animate-in fade-in zoom-in-95 duration-300">
      <h2 className="text-3xl font-black dark:text-white mb-2 uppercase tracking-tighter">
        {isLogin ? "Welcome Back" : "Create Account"}
      </h2>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-8">
        {isLogin
          ? "Enter your credentials to enter"
          : "Join the task management system"}
      </p>

      <form onSubmit={handleUserAuth} className="space-y-4">
        <div className="space-y-1">
          <input
            type="email"
            placeholder="Email Address"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 dark:text-white outline-none border-2 border-transparent focus:border-indigo-500 font-bold transition-all"
          />
        </div>
        <div className="space-y-1">
          <input
            type="password"
            placeholder="Password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-4 rounded-2xl bg-slate-50 dark:bg-slate-800 dark:text-white outline-none border-2 border-transparent focus:border-indigo-500 font-bold transition-all"
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 p-3 rounded-xl">
            <p className="text-red-500 text-[10px] font-black uppercase flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i> {error}
            </p>
          </div>
        )}

        <button className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest active:scale-95 shadow-lg shadow-indigo-500/20 transition-all">
          {isLogin ? "Login" : "Register Profile"}
        </button>
      </form>

      <div className="relative my-8 text-center text-[10px] font-black uppercase text-slate-400">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-100 dark:border-slate-800"></div>
        </div>
        <span className="relative bg-white dark:bg-slate-900 px-4">
          Secure Social Link
        </span>
      </div>

      <button
        onClick={handleGoogleLogin}
        className="w-full py-4 border-2 border-slate-100 dark:border-slate-800 rounded-2xl flex items-center justify-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all active:scale-95 group"
      >
        {/* <img
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/smartlock/google.svg"
          alt="Google"
          className="w-5 h-5 group-hover:scale-110 transition-transform"
        /> */}
        <span className="font-black text-xs dark:text-white uppercase tracking-widest">
          Continue with Google
        </span>
      </button>

      <p className="text-center mt-8 text-[11px] text-slate-500 font-bold uppercase tracking-tight">
        {isLogin ? "Don't have an account?" : "Already have an account?"}
        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError("");
          }}
          className="ml-2 text-indigo-600 font-black hover:underline"
        >
          {isLogin ? "Sign Up Now" : "Log In"}
        </button>
      </p>
    </div>
  );
}
